import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, of, from, forkJoin, Observable } from 'rxjs';
import { map, tap, catchError, switchMap } from 'rxjs/operators';
import { AppMode } from '~/front-models/appMode';
import { UserSummary } from '~/models/administration/user';
import { MeService } from './administration.ts/me';
import { ApiServiceBase, API_NAMES } from './api';
import { MenuService, MenuItem } from './administration.ts/menu';
import { CacheResolverService } from './modules/common/Caching/cacheResolverService';

@Injectable({ providedIn: 'root' })
export class SecurityService extends ApiServiceBase {
  private userSubject: BehaviorSubject<UserSummary>;
  private permissionsSubject: BehaviorSubject<string[]>;
  private licenses: any;
  baseLicense: any;
  pendingRequests: any[] = [];
  isRefreshingToken = false;
  refreshTokenInterval: any;

  constructor(injector: Injector,
    private router: Router,
    private http: HttpClient,
    private _meSerivce: MeService,
    private _menuService: MenuService,
    private _cacheResolverService: CacheResolverService) {
    super(injector);
    this.setBase(API_NAMES.identity);

    this.userSubject = new BehaviorSubject<UserSummary>(null);
    this.permissionsSubject = new BehaviorSubject<string[]>([]);
  }

  public get currentUser(): UserSummary {
    return this.userSubject.value;
  }

  setCurrentUser() {
    return this._meSerivce.getUser().pipe(map((user) => {
      this.userSubject.next(user);
      if (user) {
        try {
          localStorage.setItem('currentUser', JSON.stringify(user));
        } catch {}
      }
      return user;
    }));
  }

  public get permissions(): string[] {
    return this.permissionsSubject.value;
  }

  login(model: any) {
    return this.http.post(this._url(), model);
  }

  verifyCode(model: any) {
    return this.http.post(this._url(`/verify-code`), model);
  }

  getCustomerUserCredentials(model: any) {
    return this.client.post<any>(this._url(`/customer-credentials`), model);
  }

  saveLoggedInAccountSite(model: any) {
    return this.client.post<any>(this._url(`/loggedin-accountsite`), model);
  }

  getLicense() {
    return this.client.get(this._url(`/getSPLicense`));
  }

  getMobileDeviceSetting() {
    return this.client.get(this._url(`/getMobileDeviceSetting`));
  }

  getBaseLicense() {
    return this.client.get(this._url(`/get-baseLicense`));
  }

  clearCache() {
    // Clear the HTTP response cache
    this._cacheResolverService.clear();

    this._menuService.clearMenuCache();

    // Clear all menu, dashboard, and user specific cached keys in localStorage
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('sidebar_menu_') || 
        key.startsWith('admin_menu_') ||
        key.startsWith('enterprise-current-dashboard-') ||
        key === 'enterprise_user_accounts'
      )) {
        localStorage.removeItem(key);
      }
    }
  }

  removeLocalCredentials(withoutNav?: boolean) {
    this.clearCache();
    if (this.refreshTokenInterval) {
      clearInterval(this.refreshTokenInterval);
      this.refreshTokenInterval = null;
    }

    localStorage.removeItem('loggedInAccountSites');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    this.userSubject.next(null);
    this.licenses = null;
    this.baseLicense = null;
    this.pendingRequests = [];
    this.isRefreshingToken = false;

    if (!withoutNav) {
      this.router.navigate(['/login']);
    }
  }

  removeOldSession(model: any) {
    return this.http.post(this._url(`/logout`), model);
  }

  forceLogout(model: any) {
    return this.http.post(this._url(`/force-logout`), model);
  }

  logout() {
    return this.http.post(this._url(`/logout-auth`), {});
  }

  refreshToken() {
    const rawAccessToken = localStorage.getItem('accessToken');
    const rawRefreshToken = localStorage.getItem('refreshToken');

    if (!rawAccessToken || !rawRefreshToken) {
      this.removeLocalCredentials();
      return;
    }

    let parsedAccessToken: any;
    let parsedRefreshToken: any;

    try {
      parsedAccessToken = JSON.parse(rawAccessToken);
      parsedRefreshToken = JSON.parse(rawRefreshToken);
    } catch {
      this.removeLocalCredentials();
      return;
    }

    const token = parsedAccessToken?.token || (typeof parsedAccessToken === 'string' ? parsedAccessToken : null);

    if (!token || !parsedRefreshToken) {
      this.removeLocalCredentials();
      return;
    }

    const model = {
      accessToken: token,
      refreshToken: parsedRefreshToken,
    };

    this.http.post<any>(this._url(`/refresh-token`), model).subscribe(
      (data) => {
        if (data?.accessToken && data?.refreshToken) {
          localStorage.setItem('accessToken', JSON.stringify(data.accessToken));
          localStorage.setItem('refreshToken', JSON.stringify(data.refreshToken));

          this.setRefreshTokenInterval(data.accessToken.expiresIn);

          this.isRefreshingToken = false;
          this.pendingRequests.forEach((oldRequest) => {
            const newRequest = oldRequest.request.clone({
              setHeaders: { Authorization: `Bearer ${data.accessToken.token}` }
            });

            oldRequest.next.handle(newRequest).subscribe();
          });

          if (!this.pendingRequests.find((x) => x.request.url.endsWith('me'))) {
            this.setCurrentUser().subscribe();
          }

          this.pendingRequests = [];
        }
        else {
          this.removeLocalCredentials();
        }
      },
      (error: any) => {
        this.removeLocalCredentials();
      });
  }

  setRefreshTokenInterval(validForInSeconds: number) {
    if (this.refreshTokenInterval) {
      clearInterval(this.refreshTokenInterval);
      this.refreshTokenInterval = null;
    }

    if (!validForInSeconds || validForInSeconds <= 60) {
      return;
    }

    this.refreshTokenInterval = setInterval(() => {
      this.refreshToken();
    }, (validForInSeconds - 60) * 1000);
  }

  private _url(v?: string | undefined) {
    v = v || '';

    return this.url(`/accounts${v}`);
  }

  hasPermission(appMode: AppMode, permission: string) {
    if (!this.permissions.length) {
      //this.initializePermissions();
      return false;
    }

    if (this.currentUser) {
      // Check if applaince
      if (this.currentUser.isEnterprise && appMode === 'APPLIANCE') {
        return false;
      }

      if (window.location.href.indexOf('/stock/accounts') > -1 && !this.currentUser.isEnterprise) {
        return false;
      }

      // Check if has permission
      if (!permission) {
        return true;
      }

      if (this.permissions.find((x) => x === permission)) {
        return true;
      }

      // Check unified permission aliases (e.g. siteTransfers <-> site-transfers <-> transfer-requests)
      const aliases = this.getUnifiedPermissionAliases(permission);
      for (const alias of aliases) {
        if (this.permissions.find((x) => x === alias)) {
          return true;
        }
      }

      if (this.currentUser?.isEnterprise && (
        permission.startsWith('site-transfers-') ||
        permission.startsWith('siteTransfers-') ||
        permission.startsWith('site-transfer-') ||
        permission.startsWith('siteTransfer-') ||
        permission.startsWith('transfer-requests-') ||
        permission.startsWith('enterprise-site-transfer') ||
        permission.startsWith('transferRequests')
      )) {
        return true;
      }
    }

    return false;
  }

  private getUnifiedPermissionAliases(permission: string): string[] {
    if (!permission) return [];
    const aliases: string[] = [];

    const lastDash = permission.lastIndexOf('-');
    if (lastDash === -1) return aliases;

    const scope = permission.substring(0, lastDash);
    const op = permission.substring(lastDash + 1);

    // Unified transfer scopes
    const siteTransferScopes = ['siteTransfers', 'site-transfers', 'siteTransferOut', 'site-transfer-out', 'siteTransferIn', 'site-transfer-in', 'transferRequests', 'transfer-requests', 'enterprise-site-transfer', 'enterprise-site-transfers'];
    if (siteTransferScopes.includes(scope)) {
      for (const s of siteTransferScopes) {
        if (s !== scope) {
          aliases.push(`${s}-${op}`);
        }
      }
    }

    // General camelCase <-> kebab-case conversions
    const kebabScope = scope.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
    const camelScope = scope.replace(/-([a-z])/g, (_, g) => g.toUpperCase());

    if (kebabScope !== scope && !aliases.includes(`${kebabScope}-${op}`)) aliases.push(`${kebabScope}-${op}`);
    if (camelScope !== scope && !aliases.includes(`${camelScope}-${op}`)) aliases.push(`${camelScope}-${op}`);

    return aliases;
  }

  initializePermissions() {
    return new Promise((resolve: any, reject) => {
      this._meSerivce.getPermissions().subscribe(
        (permissions: any) => {
          this.permissionsSubject.next(permissions);
          resolve();
        },
        (e: any) => {
          reject(e);
        }
      );
    });
  }

  licenseCheck() {
    if (this.licenses) {
      return of(this.licenses);
    }
    return this.client.get(this._url(`/getSPLicense`)).pipe(
      tap(licenses => this.licenses = licenses)
    );
  }

  isRouteAllowed(url: string): Observable<boolean> {
    const pathParts = url.split('/').filter(p => p);
    if (pathParts.length === 0) {
      return of(true);
    }

    const base = pathParts[0];
    if (base !== 'stock' && base !== 'asset') {
      return of(true);
    }

    // Allow base layout paths themselves
    const cleanUrl = url.split('?')[0].replace(/\/$/, '');
    if (cleanUrl === '/' + base || cleanUrl === base) {
      return of(true);
    }

    // Allow site transfers and related transaction routes
    if (
      cleanUrl.startsWith('/stock/site-transfers') ||
      cleanUrl.startsWith('/stock/transaction/site-transfer') ||
      cleanUrl.startsWith('/stock/transactions/site-transfer') ||
      cleanUrl.startsWith('/stock/transfer-requests') ||
      cleanUrl.startsWith('/asset/transactions/transfer-requests')
    ) {
      return of(true);
    }

    return forkJoin({
      sidebarMenu: this._menuService.getSidebarMenu(base),
      adminMenu: this._menuService.getAdminMenu(base)
    }).pipe(
      map(({ sidebarMenu, adminMenu }) => {
        const allMenuItems = [...(sidebarMenu || []), ...(adminMenu || [])];
        return this.checkMenuMatch(allMenuItems, cleanUrl);
      }),
      catchError(e => {
        console.error('Failed to verify menu permissions', e);
        return of(false);
      })
    );
  }

  private checkMenuMatch(items: MenuItem[], url: string): boolean {
    if (!items || items.length === 0) return false;
    const normalizedUrl = (url.startsWith('/') ? url : '/' + url).split('?')[0].replace(/\/$/, '');

    for (const item of items) {
      if (item.route) {
        let menuRoute = item.route.split('?')[0].replace(/\/$/, '');
        if (!menuRoute.startsWith('/')) {
          menuRoute = '/' + menuRoute;
        }
        if (normalizedUrl === menuRoute || normalizedUrl.startsWith(menuRoute + '/')) {
          return true;
        }
      }
      if (item.children && item.children.length > 0) {
        if (this.checkMenuMatch(item.children, normalizedUrl)) {
          return true;
        }
      }
    }
    return false;
  }

  hasAccessObservable(data: any): Observable<boolean> {
    if (this.permissions.length) {
      return of(this.hasPermission(data['auth'], data['permission']));
    } else {
      return from(this.initializePermissions()).pipe(
        map(() => this.hasPermission(data['auth'], data['permission'])),
        catchError(() => of(false))
      );
    }
  }

  getLicenseObservable(): Observable<any> {
    if (this.licenses && this.baseLicense) {
      return of({ licenses: this.licenses, baseLicense: this.baseLicense });
    }
    return forkJoin({
      licenses: this.licenseCheck(),
      baseLicense: this.getBaseLicense()
    }).pipe(
      tap(({ licenses, baseLicense }) => {
        this.licenses = licenses;
        this.baseLicense = baseLicense;
      }),
      catchError(e => {
        console.error('Failed to load license', e);
        this.licenses = {};
        this.baseLicense = {};
        return of({});
      })
    );
  }

  hasLicense(data: any, url?: string): boolean {
    let baseLicense = data.baseLicense;

    // Infer baseLicense from URL if not explicitly provided in route data
    if (!baseLicense && url) {
      const pathParts = url.split('/').filter(p => p);
      if (pathParts.length > 0) {
        const base = pathParts[0];
        if (base === 'stock' || base === 'asset') {
          baseLicense = base;
        }
      }
    }

    if (data.license && baseLicense) {
      if (baseLicense === "stock") {
        return !!this.licenses?.stock?.[data.license];
      }
      else if (baseLicense === "asset") {
        return !!this.licenses?.asset?.[data.license];
      }
      else {
        if (data.license === 'excelImport') {
          return !!(this.licenses?.general?.excelImport || this.licenses?.general?.textImport);
        }
        return !!this.licenses?.general?.[data.license];
      }
    }

    if (baseLicense) {
      if (baseLicense === "stock") {
        return !!this.baseLicense?.isStock;
      }
      if (baseLicense === "asset") {
        return !!this.baseLicense?.isAsset;
      }
    }

    return true;
  }
}

